'use client';
import React, { useState, useEffect } from 'react';

import { MapPin, Zap, Grid3X3, X } from 'lucide-react';
import { ServiceCard } from '@/components/ServiceCard';

interface Module {
  id: string;
  label: string;
  image: string;
  href: string;
  badge?: 'HOT' | 'NEW' | 'COMING SOON';
}

const modules: Module[] = [
  { id: 'mod-classifieds', label: 'Classifieds', image: '/assets/images/services-classifieds.png', href: '/classifieds' },
  { id: 'mod-jobs', label: 'Jobs', image: '/assets/images/services-jobs.png', href: '/jobs', badge: 'HOT' },
  { id: 'mod-dating', label: 'Dating', image: '/assets/images/services-dating.png', href: '/dating' },
  { id: 'mod-videos', label: 'Videos', image: '/assets/images/services-videos.png', href: '/videos' },
  { id: 'mod-hotels', label: 'Hotels', image: '/assets/images/services-hotels.png', href: '/hotels' },
  { id: 'mod-transport', label: 'Transport', image: '/assets/images/services-transport.png', href: '/transport' },
  { id: 'mod-food', label: 'Food', image: '/assets/images/services-food.png', href: '/food' },
  { id: 'mod-pharmacy', label: 'Pharmacy', image: '/assets/images/services-pharmacy.png', href: '/pharmacy' },
  { id: 'mod-property', label: 'Property', image: '/assets/images/services-property.png', href: '/property' },
  { id: 'mod-services', label: 'Services', image: '/assets/images/services-services.png', href: '/business' },
  { id: 'mod-business', label: 'Business', image: '/assets/images/services-business.png', href: '/business' },
  { id: 'mod-delivery', label: 'Delivery', image: '/assets/images/services-delivery.png', href: '/delivery' },
  { id: 'mod-education', label: 'Education', image: '/assets/images/services-education.png', href: '/education' },
  { id: 'mod-events', label: 'Events', image: '/assets/images/services-events.png', href: '/events' },
  { id: 'mod-news', label: 'News', image: '/assets/images/services-news.png', href: '/news' },
  { id: 'mod-auto-parts', label: 'Auto Parts', image: '/assets/images/services-auto-parts.png', href: '/auto-parts', badge: 'NEW' },
  { id: 'mod-machinery', label: 'Machinery', image: '/assets/images/services-machinery.png', href: '/machinery', badge: 'NEW' },
  { id: 'mod-beauty', label: 'Beauty & Fashion', image: '/assets/images/services-beauty-fashion.png', href: '/beauty-fashion', badge: 'NEW' },
  { id: 'mod-imports', label: 'Imported Products', image: '/assets/images/services-imports.png', href: '/imported-products', badge: 'NEW' },
];

// Coming soon placeholder names that roll
const comingSoonNames = [
  'AI Assistant', 'Flash Deals', 'Nearby Ads', 'Community', 'Market',
  'Freelance', 'Real Estate', 'Agriculture', 'Livestock', 'Tourism',
];

function ComingSoonTile({ index }: { index: number }) {
  const [nameIdx, setNameIdx] = useState(index % comingSoonNames.length);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setNameIdx(prev => (prev + 1) % comingSoonNames.length);
        setVisible(true);
      }, 300);
    }, 2200 + index * 400);
    return () => clearInterval(interval);
  }, [index]);

  return (
    <div className="module-icon-btn relative opacity-60 cursor-default select-none">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)' }}>
        <Zap size={16} style={{ color: 'rgba(255,255,255,0.25)' }} />
      </div>
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: 'rgba(0,200,83,0.6)' }}>
          Soon
        </span>
        <span
          className="text-[8px] font-medium text-center leading-tight transition-all duration-300"
          style={{
            color: 'rgba(255,255,255,0.35)',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(-4px)',
          }}>
          {comingSoonNames[nameIdx]}
        </span>
      </div>
    </div>
  );
}

// International location selector
const COUNTRIES = [
  { code: 'KE', flag: '🇰🇪', name: 'Kenya', city: 'Nairobi' },
  { code: 'TZ', flag: '🇹🇿', name: 'Tanzania', city: 'Dar es Salaam' },
  { code: 'UG', flag: '🇺🇬', name: 'Uganda', city: 'Kampala' },
  { code: 'RW', flag: '🇷🇼', name: 'Rwanda', city: 'Kigali' },
  { code: 'ET', flag: '🇪🇹', name: 'Ethiopia', city: 'Addis Ababa' },
  { code: 'NG', flag: '🇳🇬', name: 'Nigeria', city: 'Lagos' },
  { code: 'ZA', flag: '🇿🇦', name: 'South Africa', city: 'Johannesburg' },
  { code: 'GH', flag: '🇬🇭', name: 'Ghana', city: 'Accra' },
  { code: 'SE', flag: '🇸🇪', name: 'Sweden', city: 'Stockholm' },
  { code: 'GB', flag: '🇬🇧', name: 'UK', city: 'London' },
  { code: 'CN', flag: '🇨🇳', name: 'China', city: 'Beijing' },
  { code: 'US', flag: '🇺🇸', name: 'USA', city: 'New York' },
  { code: 'DE', flag: '🇩🇪', name: 'Germany', city: 'Berlin' },
  { code: 'AE', flag: '🇦🇪', name: 'UAE', city: 'Dubai' },
  { code: 'IN', flag: '🇮🇳', name: 'India', city: 'Mumbai' },
];

export default function ModuleGrid() {
  const [open, setOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);

  const visibleModules = modules.slice(0, 10);
  const comingSoonCount = Math.max(0, 10 - visibleModules.length);

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-foreground">All Services</h2>
        <div className="flex items-center gap-2">
          {/* Location selector */}
          <button
            onClick={() => setLocationOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200"
            style={{
              background: 'rgba(0,200,83,0.08)',
              border: '1px solid rgba(0,200,83,0.25)',
              color: 'var(--primary)',
            }}>
            <span>{selectedCountry.flag}</span>
            <MapPin size={11} />
            <span className="hidden sm:inline">{selectedCountry.city}</span>
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200"
            style={{
              background: open ? 'rgba(0,200,83,0.12)' : 'var(--secondary)',
              border: `1px solid ${open ? 'rgba(0,200,83,0.4)' : 'var(--border)'}`,
              color: open ? 'var(--primary)' : 'var(--muted-foreground)',
            }}>
            {open ? <X size={14} /> : <Grid3X3 size={14} />}
            {open ? 'Close' : 'All Modules'}
          </button>
        </div>
      </div>

      {/* Inline first-row preview (always visible) */}
      <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-2">
        {visibleModules.map((mod) => (
          <ServiceCard
            key={mod.id}
            name={mod.label}
            image={mod.image}
            href={mod.href}
            badge={mod.badge}
            size="sm"
          />
        ))}
        {Array.from({ length: comingSoonCount }).map((_, i) => (
          <ComingSoonTile key={`coming-soon-${i}`} index={i} />
        ))}
      </div>

      {/* Second row: remaining modules + coming soon */}
      <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-2 mt-2">
        {modules.slice(10).map((mod) => (
          <ServiceCard
            key={mod.id}
            name={mod.label}
            image={mod.image}
            href={mod.href}
            badge={mod.badge}
            size="sm"
          />
        ))}
        {Array.from({ length: Math.max(0, 10 - modules.slice(10).length) }).map((_, i) => (
          <ComingSoonTile key={`coming-soon-2-${i}`} index={i + 3} />
        ))}
      </div>

      {/* Collapsible full grid overlay */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
          <div className="fixed top-16 right-4 z-50 w-80 max-h-[70vh] overflow-y-auto rounded-2xl p-4 shadow-2xl"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-foreground">All Modules</span>
              <button onClick={() => setOpen(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
                style={{ background: 'var(--secondary)', color: 'var(--muted-foreground)' }}>
                <X size={14} />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {modules.map((mod) => (
                <ServiceCard
                  key={mod.id}
                  name={mod.label}
                  image={mod.image}
                  href={mod.href}
                  badge={mod.badge}
                  size="sm"
                />
              ))}
              {Array.from({ length: 3 }).map((_, i) => (
                <ComingSoonTile key={`full-coming-${i}`} index={i + 5} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Location Selector Modal */}
      {locationOpen && (
        <>
          <div className="fixed inset-0 z-50" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
            onClick={() => setLocationOpen(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 rounded-2xl p-5 max-h-[80vh] overflow-y-auto"
            style={{ background: 'var(--card)', border: '1px solid var(--border)', maxWidth: 400, margin: '0 auto' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-foreground">Choose Location</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>See ads near you worldwide</p>
              </div>
              <button onClick={() => setLocationOpen(false)}
                className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
                <X size={14} style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  onClick={() => { setSelectedCountry(country); setLocationOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-150 active:scale-95 text-left"
                  style={{
                    background: selectedCountry.code === country.code ? 'rgba(0,200,83,0.1)' : 'var(--secondary)',
                    border: `1px solid ${selectedCountry.code === country.code ? 'rgba(0,200,83,0.4)' : 'var(--border)'}`,
                  }}>
                  <span className="text-2xl">{country.flag}</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{country.name}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{country.city}</p>
                  </div>
                  {selectedCountry.code === country.code && (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--primary)' }}>
                      <span className="text-[10px] text-black font-bold">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}